// IMPORTS
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import useApiEp from "../../hooks/useApiEp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  getErrorResponse,
  getHeader,
  getToken,
  launchEsSignup,
  roundToMaxDecimals,
  toastError,
} from "../../utils/util";
import { UserContext } from "../../context/UserProvider";
import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageCropperModal from "../../widgets/ImageCropperModal";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { FaWallet, FaCoins } from "react-icons/fa";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { SiGooglesheets } from "react-icons/si";
import { GlassButton } from "../../themes/UiElements";
import { Phone, Settings2Icon } from "lucide-react";

// STYLES
const styles = {
  details: {
    padding: "1rem",
    borderTop: "1px solid #e1e1e1",
    display: "flex",
    alignItems: "center",
  },
  value: {
    padding: "1rem 2rem",
    borderTop: "1px solid #e1e1e1",
    color: "#899499",
  },
};

export default function ProfileCard(props) {
  const [showUploadPic, setShowUploadPic] = useState(false);
  const [connectingSheet, setConnectingSheet] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [fetchingCustomerId, setFetchingCustomerId] = useState(false);
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-account-balance`
  );
  const navigate = useNavigate();
  const { user, loading, reloadUser } = useContext(UserContext);

  const { data: MainData } = useApiEp("/user/get-spreadsheet-link");

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/spreadsheets",
    onSuccess: async (codeResponse) => {
      setConnectingSheet(true);
      try {
        const response = await axios.post(
          `${API_ENDPOINT}/api/google-auth`,
          {
            code: codeResponse.code,
          },
          getHeader()
        );
        toast.success(
          response.data.message || "Google Sheets connected successfully!"
        );
        document.location.reload();
        console.log("Server response:", response.data);
      } catch (err) {
        toastError(err);
      } finally {
        setConnectingSheet(false);
      }
    },
    onError: (errorResponse) => {
      console.log(errorResponse);
      toast.error("Failed to connect Google Sheets");
      setConnectingSheet(false);
    },
  });

  const handlePhoneOptionClick = async (option) => {
    setShowPhoneDialog(false);
    setFetchingCustomerId(true);

    try {
      const response = await axios.get(
        `${API_ENDPOINT}/root/get-cusid-for-onboarding`,
        getHeader()
      );

      const customerId = response.data.customerId;

      if (option === "Option A") {
        launchEsSignup(false, customerId);
      } else if (option === "Option B") {
        launchEsSignup(true, customerId);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setFetchingCustomerId(false);
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    return <Alert severity="warning">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Card variant="outlined">
      <ImageCropperModal
        open={showUploadPic}
        onClose={() => setShowUploadPic(false)}
        aspectRatio={1 / 1}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}/upload/upload-profile-image`}
        headers={{ Authorization: `Bearer ${getToken()}` }}
        onSuccess={(response) => {
          toast.success(response.message);
          reloadUser();
        }}
        onError={(error) => {
          toastError(error);
        }}
      />

      {/* PHONE NUMBER OPTIONS DIALOG */}
      <Dialog
        open={showPhoneDialog}
        onClose={() => setShowPhoneDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Phone style={{ fontSize: 48, color: "#1976d2", marginBottom: 8 }} />
          <Typography variant="h5" fontWeight={600}>
            Add Phone Numbers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose an option to add your phone numbers
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handlePhoneOptionClick("Option A")}
              disabled={fetchingCustomerId}
              sx={{
                py: 2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              {fetchingCustomerId ? "Loading..." : "Option A"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handlePhoneOptionClick("Option B")}
              disabled={fetchingCustomerId}
              sx={{
                py: 2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              {fetchingCustomerId ? "Loading..." : "Option B"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pt: 2, pb: 1 }}>
          <Button
            onClick={() => setShowPhoneDialog(false)}
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item sx={{ p: "1.5rem 0rem", textAlign: "center" }}>
          {/* PROFILE PHOTO */}
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <IconButton
                onClick={() => {
                  setShowUploadPic(true);
                }}
                sx={{
                  border: "4px solid white",
                  backgroundColor: "#ff558f",
                  width: 32,
                  height: 32,
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "#ff3370",
                  },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 18, color: "white" }} />
              </IconButton>
            }
          >
            <Avatar
              sx={{ width: 100, height: 100, mb: 1.5 }}
              src={user?.profileImage}
            ></Avatar>
          </Badge>
          <Typography variant="h6">{user?.agentName}</Typography>
          <Typography color="text.secondary">{user?.userType}</Typography>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <Typography style={styles.details}>
              <FaWallet style={{ marginRight: "8px", color: "#899499" }} />
              Wallet
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "end" }}>
            <Link to={`/home/transaction-history`}>
              <Typography style={styles.value}>
                {data?.wallet ? (
                  <Chip
                    label={`${roundToMaxDecimals(data.wallet, 2)}`}
                    variant="outlined"
                    size="small"
                    color="info"
                  />
                ) : (
                  "NA"
                )}
              </Typography>
            </Link>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <Typography style={styles.details}>
              <FaCoins style={{ marginRight: "8px", color: "#899499" }} />
              AI Credits
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "end" }}>
            <Typography style={styles.value}>
              <Chip
                label={roundToMaxDecimals(data?.aiWallet ?? 0)}
                variant="outlined"
                size="small"
                color="info"
              />
            </Typography>
          </Grid>
        </Grid>

        {/* GOOGLE SHEETS INTEGRATION */}
        <Grid item xs={12}>
          {user?.googleSheetVerified ? (
            <Alert
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              severity="success"
              sx={{
                borderRadius: 2,
                alignItems: "center",
              }}
            >
              <AlertTitle>Google Sheets Connected</AlertTitle>
              Your chats are successfully linked and syncing with Google Sheets.
              <Box sx={{ mt: 1 }}>
                {MainData?.link ? (
                  <Button
                    component="a"
                    href={MainData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<SiGooglesheets />}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Open Spreadsheet
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<SiGooglesheets />}
                    onClick={googleLogin}
                    disabled={connectingSheet}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    {connectingSheet
                      ? "Connecting..."
                      : "Grant Spreadsheet Permission"}
                  </Button>
                )}
              </Box>
              <Box mt={2}>
                <GlassButton
                  onClick={() => {
                    navigate(`/home/sheets-schema-design`);
                  }}
                  startIcon={<Settings2Icon />}
                >
                  Schema Settings
                </GlassButton>
              </Box>
            </Alert>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<SiGooglesheets />}
              onClick={googleLogin}
              disabled={connectingSheet}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1,
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "rgba(52, 168, 83, 0.08)",
                },
              }}
            >
              {connectingSheet
                ? "Connecting..."
                : "Integrate Chats to Google Sheets"}
            </Button>
          )}
        </Grid>
        <Grid item xs={12}>
          <GlassButton
            sx={{ my: 2 }}
            startIcon={<Phone />}
            onClick={() => setShowPhoneDialog(true)}
          >
            Add Phone numbers
          </GlassButton>
        </Grid>
      </Grid>
    </Card>
  );
}
